/**
 * Admin > 역할 관리 페이지 (V42)
 *
 * 관리자가 직원의 역할을 관리(부여/제거)할 수 있는 페이지입니다.
 *
 * **탭 구성:**
 * - 역할 부여: 선택한 직원에게 역할 일괄 부여
 * - 역할 제거: 역할 일괄 비우기 + 개별 선택 삭제
 *
 * **필터:**
 * - 현재 역할, 조직, 직책, 직위, 고용형태
 *
 * **정책:**
 * - userType='admin'만 접근 가능
 * - system_admin 역할은 UI에서 노출/수정 불가
 */

/**
 * Admin > 역할 관리 페이지 (V88)
 *
 * V88 변경: Firestore 직접 읽기 → httpsCallable 전환
 */

import React, { useState, useEffect, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import {
  Loader,
  AlertCircle,
  CheckCircle,
  Users,
  Shield,
  UserPlus,
  UserMinus,
  Filter,
  X,
  ChevronDown,
  Search,
} from 'lucide-react';
import { ASSIGNABLE_ROLE_IDS, ROLE_LABEL_MAP } from '../../shared/constants/roles';
import { useOrgUnitsPublic } from '../../shared/hooks/useOrgUnitsPublic';
import { useOrgUnitSubtreeFilter } from '../../shared/hooks/useOrgUnitSubtreeFilter';

// ============================================
// 타입 정의
// ============================================

interface EmployeeForRoles {
  id: string;
  nameKo: string;
  email: string;
  employeeNumber?: string;
  orgUnitId?: string;
  orgUnitName?: string;
  positionId?: string;
  positionTitle?: string;
  titleId?: string;
  jobTitle?: string;
  currentEmploymentType?: string;
  roleIds: string[];
  assignedRoleIds?: string[]; // V26.3: SSoT 기반 역할
  userType?: 'admin' | 'employee';
}

/**
 * V26.3: 역할 표시용 헬퍼 함수
 * assignedRoleIds 우선, roleIds fallback
 */
function getEffectiveRoleIds(emp: EmployeeForRoles): string[] {
  return emp.assignedRoleIds ?? emp.roleIds;
}

type TabType = 'assign' | 'remove';

interface FilterState {
  roleId: string;
  orgUnitId: string;
  jobTitle: string;
  positionTitle: string;
  employmentType: string;
}

// ============================================
// 상수
// ============================================

const TABS: { id: TabType; label: string; icon: React.ReactNode }[] = [
  { id: 'assign', label: '역할 부여', icon: <UserPlus size={18} /> },
  { id: 'remove', label: '역할 제거', icon: <UserMinus size={18} /> },
];

// system_admin을 제외한 역할 목록
const DISPLAY_ROLE_IDS = ASSIGNABLE_ROLE_IDS.filter((id) => id !== 'system_admin');

// ============================================
// 메인 컴포넌트
// ============================================

export function AdminRolesPage() {
  // ========================================
  // State
  // ========================================
  const [employees, setEmployees] = useState<EmployeeForRoles[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 탭 상태
  const [activeTab, setActiveTab] = useState<TabType>('assign');

  // 필터 상태
  const [filters, setFilters] = useState<FilterState>({
    roleId: '',
    orgUnitId: '',
    jobTitle: '',
    positionTitle: '',
    employmentType: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  // V42.2: 검색 상태
  const [searchText, setSearchText] = useState('');

  // 선택 상태 (부여/제거 공통)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // 역할 부여 탭 - 부여할 역할
  const [rolesToAssign, setRolesToAssign] = useState<Set<string>>(new Set());

  // 역할 제거 탭 - 선택 삭제용
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeForRoles | null>(null);
  const [rolesToRemove, setRolesToRemove] = useState<Set<string>>(new Set());

  // 작업 상태
  const [isSaving, setIsSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<{ success: boolean; message: string } | null>(null);

  // V73.2: 조직 subtree 필터 (공통 훅)
  const { orgUnits: allOrgUnits } = useOrgUnitsPublic({ isActive: true });
  const { descendantOrgUnitIdsSet, orgDropdownOptions } = useOrgUnitSubtreeFilter(allOrgUnits, filters.orgUnitId);

  // ========================================
  // 데이터 로드
  // ========================================
  useEffect(() => {
    fetchEmployees();
  }, []);

  /**
   * V88: 직원 목록 조회 (httpsCallable 전환)
   *
   * Firestore 직접 읽기 → getEmployeesForAdmin Function 호출로 전환
   */
  const fetchEmployees = async () => {
    // Firebase Functions 초기화 확인
    if (!functions) {
      setError('Firebase Functions가 초기화되지 않았습니다.');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // V88: httpsCallable 호출
      const getEmployeesForAdmin = httpsCallable<
        { searchText?: string; status?: string },
        {
          success: boolean;
          employees: Array<{
            id: string;
            nameKo: string;
            email: string;
            employeeNumber?: string;
            orgUnitId?: string;
            orgUnitName?: string;
            positionId?: string;
            positionTitle?: string;
            titleId?: string;
            jobTitle?: string;
            currentEmploymentType?: string;
            roleIds: string[];
            assignedRoleIds?: string[]; // V26.3: SSoT 기반 역할
            userType?: string;
            status: string;
          }>;
        }
      >(functions!, 'getEmployeesForAdmin');

      const result = await getEmployeesForAdmin({});

      if (result.data.success) {
        // Backend에서 이미 필터링/정렬된 상태로 반환
        setEmployees(
          result.data.employees.map((emp) => ({
            ...emp,
            userType: emp.userType as 'admin' | 'employee' | undefined,
          }))
        );
      }

      setIsLoading(false);
    } catch (err: any) {
      console.error('직원 목록 조회 실패:', err);
      setError(err.message || '직원 목록 조회 중 오류가 발생했습니다.');
      setIsLoading(false);
    }
  };

  // ========================================
  // 필터 옵션 추출
  // ========================================
  const filterOptions = useMemo(() => {
    const roles = new Set<string>();
    const orgs = new Set<string>();
    const titles = new Set<string>();
    const positions = new Set<string>();
    const empTypes = new Set<string>();

    employees.forEach((emp) => {
      // V26.3: assignedRoleIds 우선 사용
      getEffectiveRoleIds(emp).forEach((r) => {
        if (r !== 'system_admin') roles.add(r);
      });
      if (emp.orgUnitName) orgs.add(emp.orgUnitName);
      if (emp.jobTitle) titles.add(emp.jobTitle);
      if (emp.positionTitle) positions.add(emp.positionTitle);
      if (emp.currentEmploymentType) empTypes.add(emp.currentEmploymentType);
    });

    return {
      roles: Array.from(roles).sort(),
      orgs: Array.from(orgs).sort(),
      titles: Array.from(titles).sort(),
      positions: Array.from(positions).sort(),
      empTypes: Array.from(empTypes).sort(),
    };
  }, [employees]);

  // ========================================
  // 필터링된 직원 목록
  // ========================================
  const filteredEmployees = useMemo(() => {
    return employees.filter((emp) => {
      // V42.2: 검색 필터 (이름 또는 이메일)
      if (searchText) {
        const lowerSearch = searchText.toLowerCase();
        const nameMatch = emp.nameKo.toLowerCase().includes(lowerSearch);
        const emailMatch = emp.email.toLowerCase().includes(lowerSearch);
        if (!nameMatch && !emailMatch) return false;
      }
      // V26.3: assignedRoleIds 우선 사용
      if (filters.roleId && !getEffectiveRoleIds(emp).includes(filters.roleId)) return false;
      if (descendantOrgUnitIdsSet && !descendantOrgUnitIdsSet.has(emp.orgUnitId || '')) return false;
      if (filters.jobTitle && emp.jobTitle !== filters.jobTitle) return false;
      if (filters.positionTitle && emp.positionTitle !== filters.positionTitle) return false;
      if (filters.employmentType && emp.currentEmploymentType !== filters.employmentType) return false;
      return true;
    });
  }, [employees, filters, searchText, descendantOrgUnitIdsSet]);

  // ========================================
  // 통계
  // ========================================
  const stats = useMemo(() => {
    // V26.3: assignedRoleIds 우선 사용
    const noRolesCount = filteredEmployees.filter((e) => getEffectiveRoleIds(e).length === 0).length;
    const hasRolesCount = filteredEmployees.length - noRolesCount;
    return { total: filteredEmployees.length, noRoles: noRolesCount, hasRoles: hasRolesCount };
  }, [filteredEmployees]);

  // ========================================
  // 필터 핸들러
  // ========================================
  const handleFilterChange = (key: keyof FilterState, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setSelectedIds(new Set());
  };

  const handleResetFilters = () => {
    setFilters({
      roleId: '',
      orgUnitId: '',
      jobTitle: '',
      positionTitle: '',
      employmentType: '',
    });
    setSelectedIds(new Set());
  };

  const activeFilterCount = Object.values(filters).filter((v) => v !== '').length;

  // ========================================
  // 선택 핸들러 (공통)
  // ========================================
  const handleSelectAll = () => {
    if (selectedIds.size === filteredEmployees.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredEmployees.map((e) => e.id)));
    }
  };

  const handleSelectNoRoles = () => {
    // V26.3: assignedRoleIds 우선 사용
    const noRolesIds = filteredEmployees.filter((e) => getEffectiveRoleIds(e).length === 0).map((e) => e.id);
    setSelectedIds(new Set(noRolesIds));
  };

  const handleToggleEmployee = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  // ========================================
  // 역할 부여 핸들러
  // ========================================
  const handleToggleRoleToAssign = (roleId: string) => {
    const newSet = new Set(rolesToAssign);
    if (newSet.has(roleId)) {
      newSet.delete(roleId);
    } else {
      newSet.add(roleId);
    }
    setRolesToAssign(newSet);
  };

  const handleAssignRoles = async () => {
    if (selectedIds.size === 0) {
      setSaveResult({ success: false, message: '선택된 직원이 없습니다.' });
      return;
    }
    if (rolesToAssign.size === 0) {
      setSaveResult({ success: false, message: '부여할 역할을 선택해주세요.' });
      return;
    }
    if (!functions) {
      setSaveResult({ success: false, message: 'Firebase Functions가 초기화되지 않았습니다.' });
      return;
    }

    try {
      setIsSaving(true);
      setSaveResult(null);

      const bulkAssignRoles = httpsCallable<
        { userIds: string[]; roleIds: string[] },
        { success: boolean; message: string; updatedCount: number }
      >(functions!, 'bulkAssignRolesToUsers');

      const result = await bulkAssignRoles({
        userIds: Array.from(selectedIds),
        roleIds: Array.from(rolesToAssign),
      });

      setSaveResult({ success: result.data.success, message: result.data.message });

      if (result.data.success) {
        await fetchEmployees();
        setSelectedIds(new Set());
        setRolesToAssign(new Set());
      }
    } catch (err: any) {
      console.error('역할 부여 실패:', err);
      setSaveResult({ success: false, message: err.message || '역할 부여 중 오류가 발생했습니다.' });
    } finally {
      setIsSaving(false);
    }
  };

  // ========================================
  // 역할 제거 핸들러 - 일괄 비우기
  // ========================================
  const handleClearRoles = async () => {
    if (selectedIds.size === 0) {
      setSaveResult({ success: false, message: '선택된 직원이 없습니다.' });
      return;
    }
    if (!functions) {
      setSaveResult({ success: false, message: 'Firebase Functions가 초기화되지 않았습니다.' });
      return;
    }

    const confirmed = window.confirm(
      `${selectedIds.size}명의 직원 역할을 모두 비우시겠습니까?\n(system_admin 역할은 유지됩니다)`
    );
    if (!confirmed) return;

    try {
      setIsSaving(true);
      setSaveResult(null);

      const bulkClearRoles = httpsCallable<
        { userIds: string[] },
        { success: boolean; message: string; updatedCount: number }
      >(functions!, 'bulkClearRolesFromUsers');

      const result = await bulkClearRoles({ userIds: Array.from(selectedIds) });

      setSaveResult({ success: result.data.success, message: result.data.message });

      if (result.data.success) {
        await fetchEmployees();
        setSelectedIds(new Set());
      }
    } catch (err: any) {
      console.error('역할 비우기 실패:', err);
      setSaveResult({ success: false, message: err.message || '역할 비우기 중 오류가 발생했습니다.' });
    } finally {
      setIsSaving(false);
    }
  };

  // ========================================
  // 역할 제거 핸들러 - 개별 선택 삭제
  // ========================================
  const handleOpenRemovePanel = (employee: EmployeeForRoles) => {
    setSelectedEmployee(employee);
    setRolesToRemove(new Set());
  };

  const handleCloseRemovePanel = () => {
    setSelectedEmployee(null);
    setRolesToRemove(new Set());
  };

  const handleToggleRoleToRemove = (roleId: string) => {
    const newSet = new Set(rolesToRemove);
    if (newSet.has(roleId)) {
      newSet.delete(roleId);
    } else {
      newSet.add(roleId);
    }
    setRolesToRemove(newSet);
  };

  const handleRemoveSelectedRoles = async () => {
    if (!selectedEmployee) return;
    if (rolesToRemove.size === 0) {
      setSaveResult({ success: false, message: '제거할 역할을 선택해주세요.' });
      return;
    }
    if (!functions) {
      setSaveResult({ success: false, message: 'Firebase Functions가 초기화되지 않았습니다.' });
      return;
    }

    try {
      setIsSaving(true);
      setSaveResult(null);

      const removeRoles = httpsCallable<
        { userId: string; roleIds: string[] },
        { success: boolean; message: string }
      >(functions!, 'removeRolesFromUser');

      const result = await removeRoles({
        userId: selectedEmployee.id,
        roleIds: Array.from(rolesToRemove),
      });

      setSaveResult({ success: result.data.success, message: result.data.message });

      if (result.data.success) {
        await fetchEmployees();
        handleCloseRemovePanel();
      }
    } catch (err: any) {
      console.error('역할 제거 실패:', err);
      setSaveResult({ success: false, message: err.message || '역할 제거 중 오류가 발생했습니다.' });
    } finally {
      setIsSaving(false);
    }
  };

  // ========================================
  // 탭 변경 시 상태 초기화
  // ========================================
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setSelectedIds(new Set());
    setRolesToAssign(new Set());
    setSelectedEmployee(null);
    setRolesToRemove(new Set());
    setSaveResult(null);
    // V42.2: 검색어도 초기화
    setSearchText('');
  };

  // ========================================
  // 렌더링 - 로딩
  // ========================================
  if (isLoading) {
    return (
      <div style={{ padding: '60px 20px', textAlign: 'center' }}>
        <Loader size={40} style={{ color: 'var(--color-primary)', animation: 'spin 1s linear infinite' }} />
        <p style={{ marginTop: '16px', color: 'var(--color-gray-600)' }}>직원 목록을 불러오는 중...</p>
      </div>
    );
  }

  // ========================================
  // 렌더링 - 에러
  // ========================================
  if (error) {
    return (
      <div style={{ padding: '60px 20px', textAlign: 'center', background: 'white', borderRadius: '8px', border: '1px solid var(--color-error)' }}>
        <AlertCircle size={40} style={{ color: 'var(--color-error)' }} />
        <h2 style={{ marginTop: '16px', color: 'var(--color-error)' }}>오류 발생</h2>
        <p style={{ marginTop: '8px', color: 'var(--color-gray-600)' }}>{error}</p>
        <button onClick={fetchEmployees} style={{ marginTop: '16px', padding: '10px 20px', background: 'var(--color-primary)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
          다시 시도
        </button>
      </div>
    );
  }

  // ========================================
  // 렌더링 - 메인
  // ========================================
  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
      {/* 페이지 헤더 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Shield size={28} style={{ color: 'var(--color-primary)' }} />
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--color-gray-800)' }}>역할 관리</h1>
            <p style={{ fontSize: '14px', color: 'var(--color-gray-500)' }}>직원의 시스템 역할을 부여하거나 제거합니다.</p>
          </div>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div style={{ display: 'flex', gap: '4px', borderBottom: '2px solid var(--color-gray-200)', marginBottom: '24px' }}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab.id ? '3px solid var(--color-primary)' : '3px solid transparent',
              color: activeTab === tab.id ? 'var(--color-primary)' : 'var(--color-gray-600)',
              fontWeight: activeTab === tab.id ? '600' : '500',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* 통계 카드 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
        <StatCard label="전체 직원" value={stats.total} color="var(--color-gray-600)" />
        <StatCard label="역할 미부여" value={stats.noRoles} color="var(--color-warning)" highlight={stats.noRoles > 0} />
        <StatCard label="역할 부여됨" value={stats.hasRoles} color="var(--color-success)" />
      </div>

      {/* 필터 패널 */}
      <div style={{ background: 'white', borderRadius: '8px', padding: '16px', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
          {/* V42.2: 검색창 */}
          <div style={{ position: 'relative', flex: 1, maxWidth: '320px' }}>
            <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-gray-400)' }} />
            <input
              type="text"
              placeholder="이름 또는 이메일로 검색..."
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setSelectedIds(new Set());
              }}
              style={{
                width: '100%',
                padding: '10px 12px 10px 40px',
                border: '1px solid var(--color-gray-200)',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.2s',
              }}
              onFocus={(e) => { e.target.style.borderColor = 'var(--color-primary)'; }}
              onBlur={(e) => { e.target.style.borderColor = 'var(--color-gray-200)'; }}
            />
            {searchText && (
              <button
                onClick={() => { setSearchText(''); setSelectedIds(new Set()); }}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'var(--color-gray-200)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <X size={12} style={{ color: 'var(--color-gray-600)' }} />
              </button>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={() => setShowFilters(!showFilters)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                background: showFilters ? 'var(--color-primary-lighter)' : 'var(--color-gray-50)',
                border: '1px solid var(--color-gray-200)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
              }}
            >
              <Filter size={16} />
              <span>필터</span>
              {activeFilterCount > 0 && (
                <span style={{ background: 'var(--color-primary)', color: 'white', borderRadius: '10px', padding: '2px 8px', fontSize: '12px' }}>
                  {activeFilterCount}
                </span>
              )}
              <ChevronDown size={16} style={{ transform: showFilters ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
            </button>

            {activeFilterCount > 0 && (
              <button
                onClick={handleResetFilters}
                style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 12px', background: 'transparent', border: 'none', color: 'var(--color-gray-500)', cursor: 'pointer', fontSize: '14px' }}
              >
                <X size={14} />
                필터 초기화
              </button>
            )}
          </div>
        </div>

        {showFilters && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', marginTop: '16px' }}>
            <FilterSelect
              label="현재 역할"
              value={filters.roleId}
              onChange={(v) => handleFilterChange('roleId', v)}
              options={filterOptions.roles.map((r) => ({ value: r, label: ROLE_LABEL_MAP[r] || r }))}
            />
            <FilterSelect
              label="조직"
              value={filters.orgUnitId}
              onChange={(v) => handleFilterChange('orgUnitId', v)}
              options={orgDropdownOptions
                .map((ou) => ({ value: ou.id, label: '　'.repeat(ou.level || 0) + ou.name }))}
            />
            <FilterSelect
              label="직책"
              value={filters.jobTitle}
              onChange={(v) => handleFilterChange('jobTitle', v)}
              options={filterOptions.titles.map((t) => ({ value: t, label: t }))}
            />
            <FilterSelect
              label="직위"
              value={filters.positionTitle}
              onChange={(v) => handleFilterChange('positionTitle', v)}
              options={filterOptions.positions.map((p) => ({ value: p, label: p }))}
            />
            <FilterSelect
              label="고용형태"
              value={filters.employmentType}
              onChange={(v) => handleFilterChange('employmentType', v)}
              options={filterOptions.empTypes.map((e) => ({ value: e, label: e }))}
            />
          </div>
        )}
      </div>

      {/* 탭 콘텐츠 */}
      {activeTab === 'assign' && (
        <AssignTabContent
          employees={filteredEmployees}
          selectedIds={selectedIds}
          rolesToAssign={rolesToAssign}
          isSaving={isSaving}
          saveResult={saveResult}
          stats={stats}
          onSelectAll={handleSelectAll}
          onSelectNoRoles={handleSelectNoRoles}
          onToggleEmployee={handleToggleEmployee}
          onToggleRole={handleToggleRoleToAssign}
          onAssign={handleAssignRoles}
        />
      )}

      {activeTab === 'remove' && (
        <RemoveTabContent
          employees={filteredEmployees}
          selectedIds={selectedIds}
          selectedEmployee={selectedEmployee}
          rolesToRemove={rolesToRemove}
          isSaving={isSaving}
          saveResult={saveResult}
          stats={stats}
          onSelectAll={handleSelectAll}
          onToggleEmployee={handleToggleEmployee}
          onClearRoles={handleClearRoles}
          onOpenRemovePanel={handleOpenRemovePanel}
          onCloseRemovePanel={handleCloseRemovePanel}
          onToggleRoleToRemove={handleToggleRoleToRemove}
          onRemoveSelectedRoles={handleRemoveSelectedRoles}
        />
      )}
    </div>
  );
}

// ============================================
// 서브 컴포넌트 - 통계 카드
// ============================================

interface StatCardProps {
  label: string;
  value: number;
  color: string;
  highlight?: boolean;
}

function StatCard({ label, value, color, highlight }: StatCardProps) {
  return (
    <div style={{ background: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: highlight ? '2px solid var(--color-warning)' : 'none' }}>
      <div style={{ fontSize: '14px', color: 'var(--color-gray-500)', marginBottom: '8px' }}>{label}</div>
      <div style={{ fontSize: '28px', fontWeight: 'bold', color }}>{value}</div>
    </div>
  );
}

// ============================================
// 서브 컴포넌트 - 필터 드롭다운
// ============================================

interface FilterSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}

function FilterSelect({ label, value, onChange, options }: FilterSelectProps) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: '12px', color: 'var(--color-gray-500)', marginBottom: '4px' }}>{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--color-gray-200)', borderRadius: '6px', fontSize: '14px', background: 'white' }}
      >
        <option value="">전체</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
}

// ============================================
// 서브 컴포넌트 - 역할 부여 탭
// ============================================

interface AssignTabContentProps {
  employees: EmployeeForRoles[];
  selectedIds: Set<string>;
  rolesToAssign: Set<string>;
  isSaving: boolean;
  saveResult: { success: boolean; message: string } | null;
  stats: { total: number; noRoles: number; hasRoles: number };
  onSelectAll: () => void;
  onSelectNoRoles: () => void;
  onToggleEmployee: (id: string) => void;
  onToggleRole: (roleId: string) => void;
  onAssign: () => void;
}

function AssignTabContent({
  employees,
  selectedIds,
  rolesToAssign,
  isSaving,
  saveResult,
  stats,
  onSelectAll,
  onSelectNoRoles,
  onToggleEmployee,
  onToggleRole,
  onAssign,
}: AssignTabContentProps) {
  return (
    <>
      {/* 역할 선택 패널 */}
      <div style={{ background: 'white', borderRadius: '8px', padding: '20px', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--color-gray-800)', marginBottom: '12px' }}>부여할 역할 선택</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          {DISPLAY_ROLE_IDS.map((roleId) => (
            <label
              key={roleId}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                background: rolesToAssign.has(roleId) ? 'var(--color-primary-lighter)' : 'var(--color-gray-50)',
                border: rolesToAssign.has(roleId) ? '2px solid var(--color-primary)' : '2px solid var(--color-gray-200)',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <input type="checkbox" checked={rolesToAssign.has(roleId)} onChange={() => onToggleRole(roleId)} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
              <span style={{ fontWeight: rolesToAssign.has(roleId) ? '600' : '500', color: rolesToAssign.has(roleId) ? 'var(--color-primary)' : 'var(--color-gray-700)' }}>
                {ROLE_LABEL_MAP[roleId] || roleId}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* 액션 버튼 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={onSelectAll} style={{ padding: '8px 16px', background: 'white', border: '1px solid var(--color-gray-300)', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}>
            {selectedIds.size === employees.length ? '전체 해제' : '전체 선택'}
          </button>
          <button onClick={onSelectNoRoles} style={{ padding: '8px 16px', background: 'var(--color-warning)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}>
            역할 미부여만 선택 ({stats.noRoles}명)
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '14px', color: 'var(--color-gray-600)' }}>{selectedIds.size}명 선택됨</span>
          <button
            onClick={onAssign}
            disabled={isSaving || selectedIds.size === 0 || rolesToAssign.size === 0}
            style={{
              padding: '10px 24px',
              background: isSaving || selectedIds.size === 0 || rolesToAssign.size === 0 ? 'var(--color-gray-300)' : 'var(--color-primary)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: isSaving || selectedIds.size === 0 || rolesToAssign.size === 0 ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            {isSaving ? <><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> 저장 중...</> : '역할 부여'}
          </button>
        </div>
      </div>

      {/* 저장 결과 */}
      {saveResult && <ResultMessage success={saveResult.success} message={saveResult.message} />}

      {/* 직원 테이블 */}
      <EmployeeTable employees={employees} selectedIds={selectedIds} onToggle={onToggleEmployee} showRowClick={false} />
    </>
  );
}

// ============================================
// 서브 컴포넌트 - 역할 제거 탭
// ============================================

interface RemoveTabContentProps {
  employees: EmployeeForRoles[];
  selectedIds: Set<string>;
  selectedEmployee: EmployeeForRoles | null;
  rolesToRemove: Set<string>;
  isSaving: boolean;
  saveResult: { success: boolean; message: string } | null;
  stats: { total: number; noRoles: number; hasRoles: number };
  onSelectAll: () => void;
  onToggleEmployee: (id: string) => void;
  onClearRoles: () => void;
  onOpenRemovePanel: (emp: EmployeeForRoles) => void;
  onCloseRemovePanel: () => void;
  onToggleRoleToRemove: (roleId: string) => void;
  onRemoveSelectedRoles: () => void;
}

function RemoveTabContent({
  employees,
  selectedIds,
  selectedEmployee,
  rolesToRemove,
  isSaving,
  saveResult,
  stats,
  onSelectAll,
  onToggleEmployee,
  onClearRoles,
  onOpenRemovePanel,
  onCloseRemovePanel,
  onToggleRoleToRemove,
  onRemoveSelectedRoles,
}: RemoveTabContentProps) {
  return (
    <div style={{ display: 'flex', gap: '24px' }}>
      {/* 좌측: 직원 목록 */}
      <div style={{ flex: 1 }}>
        {/* 안내 */}
        <div style={{ background: '#fffbeb', border: '1px solid var(--color-warning)', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
          <p style={{ fontSize: '14px', color: '#92400e', margin: 0 }}>
            <strong>일괄 비우기:</strong> 체크박스로 직원 선택 후 [권한 비우기] 클릭<br />
            <strong>선택 삭제:</strong> 직원 이름 클릭 → 우측 패널에서 역할 선택 후 삭제
          </p>
        </div>

        {/* 액션 버튼 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={onSelectAll} style={{ padding: '8px 16px', background: 'white', border: '1px solid var(--color-gray-300)', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}>
              {selectedIds.size === employees.length ? '전체 해제' : '전체 선택'}
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '14px', color: 'var(--color-gray-600)' }}>{selectedIds.size}명 선택됨</span>
            <button
              onClick={onClearRoles}
              disabled={isSaving || selectedIds.size === 0}
              style={{
                padding: '10px 24px',
                background: isSaving || selectedIds.size === 0 ? 'var(--color-gray-300)' : '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: isSaving || selectedIds.size === 0 ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {isSaving ? <><Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> 처리 중...</> : '권한 비우기'}
            </button>
          </div>
        </div>

        {/* 저장 결과 */}
        {saveResult && <ResultMessage success={saveResult.success} message={saveResult.message} />}

        {/* 직원 테이블 */}
        <EmployeeTable
          employees={employees}
          selectedIds={selectedIds}
          onToggle={onToggleEmployee}
          showRowClick={true}
          onRowClick={onOpenRemovePanel}
          highlightedId={selectedEmployee?.id}
        />
      </div>

      {/* 우측: 선택 삭제 패널 */}
      {selectedEmployee && (
        <div style={{ width: '320px', background: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', alignSelf: 'flex-start', position: 'sticky', top: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--color-gray-800)', margin: 0 }}>선택 삭제</h3>
            <button onClick={onCloseRemovePanel} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px' }}>
              <X size={20} style={{ color: 'var(--color-gray-500)' }} />
            </button>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontWeight: '600', color: 'var(--color-gray-800)' }}>{selectedEmployee.nameKo}</div>
            <div style={{ fontSize: '13px', color: 'var(--color-gray-500)' }}>{selectedEmployee.email}</div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '13px', color: 'var(--color-gray-500)', marginBottom: '8px' }}>현재 역할 (제거할 역할 선택)</div>
            {/* V26.3: assignedRoleIds 우선 사용 */}
            {getEffectiveRoleIds(selectedEmployee).length === 0 ? (
              <div style={{ padding: '12px', background: 'var(--color-gray-50)', borderRadius: '6px', color: 'var(--color-gray-500)', fontSize: '14px' }}>부여된 역할이 없습니다.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {getEffectiveRoleIds(selectedEmployee).map((roleId) => (
                  <label
                    key={roleId}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 12px',
                      background: rolesToRemove.has(roleId) ? '#fee2e2' : 'var(--color-gray-50)',
                      border: rolesToRemove.has(roleId) ? '2px solid #ef4444' : '2px solid var(--color-gray-200)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                    }}
                  >
                    <input type="checkbox" checked={rolesToRemove.has(roleId)} onChange={() => onToggleRoleToRemove(roleId)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                    <span style={{ fontWeight: rolesToRemove.has(roleId) ? '600' : '500', color: rolesToRemove.has(roleId) ? '#dc2626' : 'var(--color-gray-700)' }}>
                      {ROLE_LABEL_MAP[roleId] || roleId}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={onRemoveSelectedRoles}
            disabled={isSaving || rolesToRemove.size === 0}
            style={{
              width: '100%',
              padding: '10px',
              background: isSaving || rolesToRemove.size === 0 ? 'var(--color-gray-300)' : '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: isSaving || rolesToRemove.size === 0 ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
            }}
          >
            {isSaving ? '처리 중...' : `선택 삭제 (${rolesToRemove.size}개)`}
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================
// 서브 컴포넌트 - 결과 메시지
// ============================================

function ResultMessage({ success, message }: { success: boolean; message: string }) {
  return (
    <div
      style={{
        padding: '12px 16px',
        marginBottom: '16px',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        background: success ? '#ecfdf5' : '#fef2f2',
        border: success ? '1px solid #10b981' : '1px solid #ef4444',
        color: success ? '#047857' : '#dc2626',
      }}
    >
      {success ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
      <span>{message}</span>
    </div>
  );
}

// ============================================
// 서브 컴포넌트 - 직원 테이블
// ============================================

interface EmployeeTableProps {
  employees: EmployeeForRoles[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  showRowClick?: boolean;
  onRowClick?: (emp: EmployeeForRoles) => void;
  highlightedId?: string;
}

function EmployeeTable({ employees, selectedIds, onToggle, showRowClick, onRowClick, highlightedId }: EmployeeTableProps) {
  return (
    <div style={{ background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'var(--color-gray-50)' }}>
            <th style={{ ...thStyle, width: '50px' }}>
              <input
                type="checkbox"
                checked={selectedIds.size === employees.length && employees.length > 0}
                onChange={() => onToggle('')}
                onClick={(e) => e.stopPropagation()}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
            </th>
            <th style={{ ...thStyle, textAlign: 'left' }}>이름</th>
            <th style={{ ...thStyle, textAlign: 'left' }}>이메일</th>
            <th style={{ ...thStyle, textAlign: 'left' }}>조직</th>
            <th style={{ ...thStyle, textAlign: 'left' }}>직위</th>
            <th style={{ ...thStyle, textAlign: 'left' }}>현재 역할</th>
          </tr>
        </thead>
        <tbody>
          {employees.map((employee) => {
            // V26.3: assignedRoleIds 우선 사용
            const effectiveRoles = getEffectiveRoleIds(employee);
            const hasNoRoles = effectiveRoles.length === 0;
            const isSelected = selectedIds.has(employee.id);
            const isHighlighted = highlightedId === employee.id;

            return (
              <tr
                key={employee.id}
                style={{
                  background: isHighlighted ? '#dbeafe' : isSelected ? 'var(--color-primary-lighter)' : hasNoRoles ? '#fffbeb' : 'white',
                  cursor: showRowClick ? 'pointer' : 'default',
                  transition: 'background 0.2s',
                }}
                onClick={() => showRowClick && onRowClick?.(employee)}
              >
                <td style={tdStyle} onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={isSelected} onChange={() => onToggle(employee.id)} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                </td>
                <td style={tdStyle}>
                  <div style={{ fontWeight: '500', color: 'var(--color-gray-800)' }}>{employee.nameKo}</div>
                </td>
                <td style={{ ...tdStyle, color: 'var(--color-gray-600)' }}>{employee.email}</td>
                <td style={{ ...tdStyle, color: 'var(--color-gray-600)' }}>{employee.orgUnitName || '-'}</td>
                <td style={{ ...tdStyle, color: 'var(--color-gray-600)' }}>{employee.positionTitle || '-'}</td>
                <td style={tdStyle}>
                  {hasNoRoles ? (
                    <span style={{ display: 'inline-block', padding: '4px 8px', background: '#fef3c7', color: '#92400e', borderRadius: '4px', fontSize: '12px', fontWeight: '500' }}>미부여</span>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {effectiveRoles.map((roleId) => (
                        <span key={roleId} style={{ display: 'inline-block', padding: '4px 8px', background: 'var(--color-primary-lighter)', color: 'var(--color-primary)', borderRadius: '4px', fontSize: '12px', fontWeight: '500' }}>
                          {ROLE_LABEL_MAP[roleId] || roleId}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {employees.length === 0 && (
        <div style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--color-gray-500)' }}>
          <Users size={48} style={{ marginBottom: '12px', opacity: 0.5 }} />
          <p>직원이 없습니다.</p>
        </div>
      )}
    </div>
  );
}

// ============================================
// 스타일
// ============================================

const thStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontSize: '13px',
  fontWeight: '600',
  color: 'var(--color-gray-600)',
  borderBottom: '1px solid var(--color-gray-200)',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontSize: '14px',
  borderBottom: '1px solid var(--color-gray-100)',
};
